import MaterialReactTable, {
  MRT_FullScreenToggleButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton,
} from 'material-react-table';
import React, { useMemo } from 'react';
import { Box, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IExpensesTransaction } from '../../apis/models/transactions';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import DownloadIcon from '@mui/icons-material/Download';

const ToggleGlobalFilter = MRT_ToggleGlobalFilterButton;
const ToggleFilters = MRT_ToggleFiltersButton;
const ShowHideColumns = MRT_ShowHideColumnsButton;
const FullScreenToggle = MRT_FullScreenToggleButton;

interface ITransactionHistoryProps {
  expenseTransactionResult: IExpensesTransaction[];
  employee_name: string;
}

const ExpenseTransactionHistory: React.FC<ITransactionHistoryProps> = ({
  expenseTransactionResult,
  employee_name,
}) => {
  const { t } = useTranslation();

  const downloadReport = () => {
    const report = new jsPDF();
    autoTable(report, {
      head: [
        [
          t('tables.date'),
          t('tables.expense_code'),
          t('tables.beneficiary_name'),
          t('tables.amount'),
          t('tables.description'),
        ],
      ],
      body: expenseTransactionResult.map((item) => {
        return [item.date, item.feeCode, item.beneficiary_name, item.fee_amount, item.description];
      }),
    });
    report.save(`${employee_name}_${t('dashboard.expenses_label')}.pdf`.toLowerCase());
  };
  const columns = useMemo(
    () => [
      {
        header: t('tables.date'),
        accessorKey: 'date',
        enableGrouping: false, //do not let this column be grouped
        size: 100,
      },
      {
        header: t('tables.expense_code'),
        accessorKey: 'feeCode',
        size: 250,
      },
      {
        header: t('tables.beneficiary_name'),
        accessorKey: 'beneficiary_name',
      },
      {
        header: t('tables.amount'),
        accessorKey: 'fee_amount',
        size: 100,
        aggregationFn: 'sum',
        AggregatedCell: ({ cell, table }) => {
          const colorValue = cell.getValue() < 0 ? 'success.main' : 'error.main';
          return (
            <>
              {t('tables.sum_by') +
                ' ' +
                table.getColumn(cell.row.groupingColumnId ?? '').columnDef.header}
              :{' '}
              <Box sx={{ color: colorValue, fontWeight: 'bold' }}>
                {cell.getValue()?.toLocaleString?.(navigator.language || 'fr-FR', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </Box>
            </>
          );
        },
      },
      {
        header: t('tables.description'),
        accessorKey: 'description',
        enableGrouping: false,
        size: 300,
      },
    ],
    [t]
  );
  return (
    <MaterialReactTable
      // @ts-ignore
      columns={columns}
      data={expenseTransactionResult}
      renderToolbarInternalActions={({ table }) => (
        <>
          <ToggleGlobalFilter table={table} />
          <IconButton onClick={downloadReport}>
            <DownloadIcon />
          </IconButton>
          <ToggleFilters table={table} />
          <ShowHideColumns table={table} />
          <FullScreenToggle table={table} />
        </>
      )}
      enableColumnResizing
      enableGrouping
      enableStickyHeader
      enableStickyFooter
      initialState={{
        density: 'compact',
        expanded: true, //expand all groups by default
        pagination: { pageIndex: 0, pageSize: 15 },
      }}
      muiToolbarAlertBannerChipProps={{ color: 'primary' }}
      muiTableContainerProps={{ sx: { maxHeight: 700 } }}
    />
  );
};
export default ExpenseTransactionHistory;
